import { UTCDate } from '@date-fns/utc';
import { TransactionType } from '@seapoint/server/enums';
import { createColumnHelper } from '@tanstack/react-table';
import { format, getTime, isSameYear } from 'date-fns';
import { AmountDisplay } from '../../../../design-system/amount-display/amount-display';
import { AvatarNameDescription } from '../../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Checkbox } from '../../../../design-system/checkbox/checkbox';
import { Stack } from '../../../../design-system/stack/stack';
import { TransactionView } from '../../../../types/entity-types';
import { getNormalizedAmount } from '../../../../utils/get-normalized-amount';

const today = new UTCDate();

const columnHelper = createColumnHelper<TransactionView>();

export const markAsPaidInvoiceTableColumnsDefinition = [
  columnHelper.display({
    id: 'select',
    cell: ({ row, table }) => {
      const shouldCheckboxBeDisabled =
        table.getIsSomeRowsSelected() &&
        table.getSelectedRowModel().rows.some((r) => r.original.id !== row.original.id);

      return (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => {
            row.toggleSelected(!!value);

            table.options.meta?.onReviewInvoice?.(row.original.id); // TODO: rename this to something that can be used across tables.
          }}
          aria-label="Select transaction"
          disabled={shouldCheckboxBeDisabled}
        />
      );
    },
    size: 5,
    minSize: 5,
  }),
  columnHelper.accessor((row) => getTime(row.fspDate), {
    id: 'date',
    header: () => <p className="text-xs font-normal leading-6 tracking-tight text-seapoint-gray-text">Date</p>,
    cell: ({ row }) => {
      const transactionDate = row.original.fspDate;
      const dateFormat = isSameYear(transactionDate, today) ? 'dd MMM' : 'dd MMM yyyy';

      return <p className="text-sm text-black">{format(transactionDate, dateFormat)}</p>;
    },
    size: 15,
    minSize: 15,
  }),
  columnHelper.accessor((row) => row.vendor?.name ?? 'Unknown', {
    id: 'vendor',
    header: () => <p className="text-xs font-normal leading-6 tracking-tight text-seapoint-gray-text">Vendor</p>,
    cell: ({ row }) => {
      const transaction = row.original;

      // AI vendor enrichment for a transaction is completed the transaction is linked to a vendor
      const vendorEnrichmentCompleted = !!transaction?.vendorId;

      const vendorName = vendorEnrichmentCompleted ? (transaction?.vendor?.name ?? '') : 'Fetching vendor details...';
      const avatarConfig = vendorEnrichmentCompleted
        ? { showAvatar: true, avatarUrl: transaction?.vendor?.logo, size: 'sm' as const }
        : { showAvatar: false };

      const isTransferOrDirectDebit =
        transaction?.type === TransactionType.TRANSFER || transaction?.type === TransactionType.DIRECT_DEBIT;

      const reference = isTransferOrDirectDebit ? transaction?.reference : null;

      return <AvatarNameDescription avatar={avatarConfig} name={vendorName} description={reference} />;
    },
    size: 30,
  }),
  columnHelper.accessor((row) => row.account.institution?.name, {
    id: 'account',
    header: () => <p className="text-xs font-normal leading-6 tracking-tight text-seapoint-gray-text">Account</p>,
    cell: ({ row }) => {
      const transactionAccount = row.original.account;

      const { name, logo } = {
        name: transactionAccount?.institution?.name,
        logo: transactionAccount?.institution?.logo,
      };

      const accountNameWithCurrency = `${name} (${transactionAccount?.currency})`;

      return (
        <AvatarNameDescription
          avatar={{ showAvatar: true, avatarUrl: logo, size: 'xs' }}
          name={accountNameWithCurrency ?? ''}
        />
      );
    },
    size: 35,
  }),
  columnHelper.accessor('amount', {
    header: () => <p className="text-xs font-normal leading-6 tracking-tight text-seapoint-gray-text">Amount</p>,
    cell: ({ row }) => {
      const normalizedAmount = getNormalizedAmount({ amount: row.original.amount, isCredit: row.original.isCredit });
      const normalizedBaseCurrencyAmount = getNormalizedAmount({
        amount: row.original.baseCurrencyAmount,
        isCredit: row.original.isCredit,
      });
      const shouldDisplayBaseCurrencyAmount = row.original.currency !== row.original.baseCurrency;

      return (
        // Arbitrary width to avoid UI jumps when paginating
        // TODO: Tanstack table has a size property, but it doesn't work as expected. Need to investigate further
        // https://github.com/TanStack/table/issues/5115
        <Stack align="end" space={2}>
          <AmountDisplay normalizedAmount={normalizedAmount} currency={row.original.currency} />

          {shouldDisplayBaseCurrencyAmount && (
            <AmountDisplay
              normalizedAmount={normalizedBaseCurrencyAmount}
              currency={row.original.baseCurrency}
              isSecondary
            />
          )}
        </Stack>
      );
    },
    size: 25,
  }),
];
